import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { AisRestService, BaseQuestionIdentifier, CaseResource, EmbeddableResourceNames, InterviewResourceIdentifier, LifeResourceIdentifier, ListDefinitionResource, LocationToIdentifierHelper, QuestionPageResource, QuestionPagesResource, QuestionSectionResource, QuestionType, ScreensQuestionResource } from '@Components/mras/core';
import { NavigationFromInterviewData, ThirdPartyNavigationDirection, ThirdPartySectionService } from '@Components/third-party-section/third-party-section.service';
import { InterviewService } from '@App/interview.service';
import { NotificationService, NotificationSeverity, ObservableService } from '@Services';
import { Subscription } from 'rxjs';
import { ScreensInterviewService } from '../screens-interview.service';
import { SharedModule } from '@Shared/shared.module';
import { QuestionHeadingComponent } from './question-heading/question-heading.component';
import { QuestionStandardComponent } from './question-standard/question-standard.component';
import { QuestionConditionalComponent } from './question-conditional/question-conditional.component';
import { QuestionCaseDataComponent } from './question-case-data/question-case-data.component';
import { FeatureManagerService } from 'app/services/global/feature-manager/feature-manager.service';
import { FeatureToggle } from '@App/enums/feature-toggle.enum';

@Component({
    selector: 'mras-interview',
    templateUrl: './screens-view.component.html',
    styleUrls: ['screens-view.component.scss'],
    imports: [SharedModule, QuestionHeadingComponent, QuestionStandardComponent, QuestionConditionalComponent, QuestionCaseDataComponent]
})
export class ScreensViewComponent implements OnInit, OnChanges, OnDestroy {
    readonly EmbeddableResourceNames = EmbeddableResourceNames;
    readonly QuestionType = QuestionType;
    questionPagesResourceIdentifier: InterviewResourceIdentifier;
    questionPagesResource: QuestionPagesResource;
    selectedInterviewPage: QuestionPageResource;
    lifeIdentifier: LifeResourceIdentifier;
    countries: ListDefinitionResource;
    caseDetails = new CaseResource();

    subscriptions: Subscription[] = [];
    sections: QuestionSectionResource[];
    bqToSection = new Map<string, ScreensQuestionResource[]>();

    thirdPartySectionSubmittedSubscription: Subscription;    
    
    showHiddenErrorMessages = false;

    _caseId: string; 
    get caseId(): string {
        return this._caseId;
    }

    @Input() 
    set caseId(value: string) {
        this._caseId = value;
    }
    @Input() interviewId: string;
    @Input() activeEnvironment: string;
    @Input() summaryMode: boolean;
    
    _pageIndex;
    get pageIndex(): number {
        return this._pageIndex;
    }
    
    @Input() 
    set pageIndex(value: number) {
        this._pageIndex = value;
    }


    constructor(public aisRestService: AisRestService, 
        public locationToIdentifier: LocationToIdentifierHelper, 
        public thirdPartySectionService: ThirdPartySectionService, 
        public interviewService: InterviewService, 
        private screensInterviewService: ScreensInterviewService,
        public notificationService: NotificationService,
        public observableService: ObservableService,
        private featureManagerService: FeatureManagerService) {        
    }
    ngOnDestroy(): void {
        this.subscriptions.forEach(s => s.unsubscribe());
    }

    ngOnInit() {
        this.summaryMode = this.interviewService.isReadOnly;
        this.showHiddenErrorMessages = this.featureManagerService.getByName(FeatureToggle.InterviewMrasShowHiddenErrorMessages).enabled;
        this.aisRestService.activeEnvironment = this.activeEnvironment;
        // this._caseId = caseId;
        // this.interviewId = interviewId;
        // this._pageIndex = pageIndex;

        this.questionPagesResourceIdentifier = {caseId: this._caseId, interviewCode: this.interviewId,  interviewInstance: "1", lifeInstance: "1", languageCode: "en"};
        this.lifeIdentifier = {caseId: this._caseId, languageCode: "en", lifeInstance: "1"};
        
        this.locationToIdentifier.identifier = this.questionPagesResourceIdentifier;
        this.locationToIdentifier.lifeResourceIdentifier = this.lifeIdentifier;

        this.subscriptions.push(this.observableService.onMrasQuestionUpdated.subscribe(() => {
            this.updateQuestionProgress();
        }));

        this.aisRestService.getBaseQuestionsInHierarchy(this.questionPagesResourceIdentifier)
            .subscribe(res => {
                res._embedded = res._embedded || ({} as [EmbeddableResourceNames, any[]]);
                const embedded = res._embedded;
                embedded[EmbeddableResourceNames.DISCLOSURES] = embedded[EmbeddableResourceNames.DISCLOSURES] || [];
                embedded[EmbeddableResourceNames.DISCLOSURE_PHYSICIANS] = embedded[EmbeddableResourceNames.DISCLOSURE_PHYSICIANS] || [];
                embedded[EmbeddableResourceNames.LISTS] = embedded[EmbeddableResourceNames.LISTS] || [];
                embedded[EmbeddableResourceNames.AFFECTED_QUESTIONS] = embedded[EmbeddableResourceNames.AFFECTED_QUESTIONS] || [];
                embedded[EmbeddableResourceNames.PHYSICIANS] = embedded[EmbeddableResourceNames.PHYSICIANS] || [];

                this.questionPagesResource = res;
                this.selectedInterviewPage = res.pages[this._pageIndex];

                this.countries = embedded[EmbeddableResourceNames.LISTS].find(e => e.listName === 'Countries');
                this.updateQuestions();

                this.updateQuestionProgress();
            });
        
        this.aisRestService.getCase(this.questionPagesResourceIdentifier)
            .subscribe(res => {
                this.caseDetails = res;
            });

        if (!this.thirdPartySectionSubmittedSubscription) {
            this.thirdPartySectionSubmittedSubscription = this.thirdPartySectionService.navigationFromInterviewObservable.subscribe(
                (data: NavigationFromInterviewData) => {

                    //validate form
                    if (data.userClickedNext) {
                        let isInvalid = false;
                        let invalidQuestions = [];
                        let invalidDisclosures = [];
                        const invalidBaseQuestions = [];

                        const page = this.questionPagesResource.pages[this._pageIndex];
                        const physicianValid = this.screensInterviewService.arePhysicianFormsValid(); 
                        
                        page?.sections?.forEach(section => {
                            invalidQuestions = section.baseQuestions.filter(x => 
                                x.visible 
                                && ((!x.answerValue || x.answerValue === null || x.answerValue === '' || x.invalid === true)
                                    || (x.answerType === "PICKLIST" && x.answerValue !== 'false' && (!x.disclosures || x.disclosures.length <=0))
                                ));

                            invalidQuestions.forEach(x => x.invalid = true);

                            if (invalidQuestions.length > 0) {
                                isInvalid = true;
                            }

                            this.questionPagesResource._embedded[EmbeddableResourceNames.DISCLOSURES].filter(disclosure =>  {
                                let disclosureOnPage = false;

                                if (!disclosure.removableFromBqCodes && !disclosure.baseQuestionsDisclosures) {
                                    return;
                                }

                                let baseQuestionDisclosureCode:string;                                
                                let baseQuestion;

                                if (!disclosure.removableFromBqCodes && disclosure.baseQuestionsDisclosures && disclosure.baseQuestionsDisclosures.length > 0) {
                                    baseQuestionDisclosureCode = disclosure.baseQuestionsDisclosures[0].baseQuestionCode;
                                }

                                if (disclosure.removableFromBqCodes && disclosure.removableFromBqCodes.length > 0){
                                    baseQuestionDisclosureCode = disclosure.removableFromBqCodes[0];
                                }

                                if (!baseQuestionDisclosureCode) {
                                    return;                                
                                }

                                this.sections.forEach(section => {
                                    baseQuestion = section.baseQuestions.find(y => y.code === baseQuestionDisclosureCode);

                                    if(this.showHiddenErrorMessages) {
                                        if (baseQuestion && baseQuestion.visible &&  baseQuestion.picklistQuestionMeta?.pickListNoneOfTheAboveVisible && baseQuestion.answerValue !== 'false'){
                                            disclosureOnPage = true;
                                        }
                                    } else {
                                        if (baseQuestion) {
                                            disclosureOnPage = true;
                                        }
                                    }

                                    if (baseQuestion && (this.showHiddenErrorMessages || baseQuestion.visible) &&  baseQuestion.picklistQuestionMeta?.pickListNoneOfTheAboveVisible && baseQuestion.answerValue !== 'false') {
                                        disclosureOnPage = true;
                                    }
                                });

                                if (!disclosureOnPage) {
                                    return;
                                }

                                const d = disclosure.questions?.filter(x => !x.answerValue || x.answerValue === null || x.answerValue === '');

                                if (d && d.length > 0) {
                                    invalidDisclosures = invalidDisclosures.concat(d);
                                    invalidBaseQuestions.push(baseQuestion);
                                }
                            });

                            invalidDisclosures?.forEach(x => x.invalid = true);

                            if (invalidDisclosures?.length > 0) {
                                isInvalid = true;
                            }
       
                            section.baseQuestions.forEach(bq => {
                                if (bq.disclosureSource === "SEARCH" && bq.visible && (!bq.disclosures || bq.disclosures.length === 0 || bq.disclosures[0]?.aliasName === '')) {
                                    bq.invalid = true;
                                    invalidBaseQuestions.push(bq);
                                    isInvalid = true;
                                }
                            });

                        });

                        if (isInvalid) {
                            this.notificationService.showNotification({ severity: NotificationSeverity.Error, message: 'Check error message(s)' });

                            if (invalidQuestions.length > 0){
                                this.scrollToTarget();

                                return;
                            }

                            if (invalidDisclosures.length > 0 && invalidDisclosures[0].code){
                                this.scrollToTarget();

                                return;
                            }

                            if (invalidBaseQuestions.length > 0){
                                this.scrollToTarget();

                                return;
                            }
                            return;
                        }

                             
                        if (!physicianValid){
                            this.notificationService.showNotification({ severity: NotificationSeverity.Error, message: 'Please fill in all required fields and submit all physicians.' });                            
                            this.scrollToTarget();
                            
                            return;
                        }

                        if (!isInvalid) {
                            this.thirdPartySectionService.thirdPartySectionNavigation(ThirdPartyNavigationDirection.Next);
                        }
                    }
                     
                    if (data.userClickedPrevious) this.thirdPartySectionService.thirdPartySectionNavigation(ThirdPartyNavigationDirection.Previous);    
                });
                
            this.subscriptions.push(this.thirdPartySectionSubmittedSubscription);
        }
    }

    scrollToTarget(): void {
        setTimeout(function () {
            const element = document.getElementsByClassName('invalid-question');
            if (element) {
                element[0].scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
        },0);
    }

    questionIdentifier(question: ScreensQuestionResource): BaseQuestionIdentifier {
        const questionIdentifier = new BaseQuestionIdentifier();
        questionIdentifier.caseId = this.questionPagesResourceIdentifier.caseId;
        questionIdentifier.languageCode = this.questionPagesResourceIdentifier.languageCode;
        questionIdentifier.interviewCode = this.questionPagesResourceIdentifier.interviewCode;
        questionIdentifier.lifeInstance = this.questionPagesResourceIdentifier.lifeInstance;
        questionIdentifier.interviewInstance = this.questionPagesResourceIdentifier.interviewInstance;
        questionIdentifier.baseQuestionCode = question.code;
        return questionIdentifier;
    }
    
    changeInterviewPage() {
        this.updateQuestions();
    }

    handleQuestionChange(changed: ScreensQuestionResource) {
        this.questionPagesResource.pages.forEach(page => {
            page.sections.forEach(section => {
                section.baseQuestions.forEach(question => {
                    if (question.code !== changed.code) {
                        return;
                    }
                    Object.keys(question).forEach(k => delete question[k]);
                    Object.assign(question, changed);
                    this.updateQuestions();
                });
            });
        });
    }

    updateQuestionProgress() {
        let totalQuestions = 0;
        let answeredQuestions = 0;

        const page = this.questionPagesResource.pages[this._pageIndex];
        page?.sections?.forEach(section => {
            section.baseQuestions.filter(x => x.visible).forEach(question => {
                totalQuestions++;
                const questionAnswered = (question.answerValue && question.answerValue !== null) 
                && ((question.answerValue !== '' && question.answerValue.toString() !== 'true') 
                    || (question.answerValue.toString() === 'true' && question.answerType === "PICKLIST" && !question.picklistQuestionMeta?.picklistItems)
                    || (question.answerValue.toString() === 'false' && question.answerType === "PICKLIST" && question.picklistQuestionMeta?.picklistItems.length > 0)
                    || (question.answerValue.toString() === 'true' && question.answerType === "ENTER_DETAILS" && question.answerText !== ''));
                if (questionAnswered) {
                    answeredQuestions++;
                }                    
            });
        });
    

        this.questionPagesResource._embedded[EmbeddableResourceNames.DISCLOSURES].forEach(disclosure => {
            let disclosureOnPage = false;
            let baseQuestionDisclosureCode = disclosure.removableFromBqCodes ? disclosure.removableFromBqCodes[0] : undefined;

            if (!baseQuestionDisclosureCode) {
                baseQuestionDisclosureCode = disclosure.category?.code;
            }

            this.sections.forEach(section => {
                const baseQuestion = section.baseQuestions.find(y => y.code === baseQuestionDisclosureCode);
                if (baseQuestion) {
                    disclosureOnPage = true;
                }
            });

            if (!disclosureOnPage) {
                return;
            }

            totalQuestions+= disclosure.questions?.length ?? 0;
            
            answeredQuestions += disclosure.questions?.filter(x => (x.answerValue && x.answerValue !== null) && ((x.answerValue !== '' && x.answerValue.toString() !== 'true') || (x.answerValue.toString() === 'true' && x.answerType === "PICKLIST" && x.disclosures?.length > 0))).length ?? 0;
        });

        this.observableService.onMrasQuestionProgress.next((answeredQuestions / totalQuestions) * 100);
    }

    updateQuestions() {
        this.bqToSection.clear();

        const sections = this.selectedInterviewPage?.sections;

        if (sections) {
            this.sections = sections;
            for (const section of this.sections) {
                this.bqToSection.set(section.code, section.baseQuestions.filter(e => e.visible === true));
            }
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        this.updateQuestions();
    }
}
